import { __extends } from "tslib";
import { DiffuseCompositeMethod } from './DiffuseCompositeMethod';
/**
 * DiffuseCelMethod provides a shading method to add diffuse cel (cartoon) shading.
 */
var DiffuseCelMethod = /** @class */ (function (_super) {
    __extends(DiffuseCelMethod, _super);
    /**
     * Creates a new DiffuseCelMethod object.
     * @param levels The amount of shadow gradations.
     * @param baseMethod An optional diffuse method on which the cartoon shading is based. If omitted, DiffuseBasicMethod is used.
     */
    function DiffuseCelMethod(levels, smoothness, baseMethod) {
        if (levels === void 0) { levels = 3; }
        if (smoothness === void 0) { smoothness = 0.1; }
        if (baseMethod === void 0) { baseMethod = null; }
        var _this = _super.call(this, baseMethod) || this;
        _this._levels = levels;
        _this._smoothness = smoothness;
        return _this;
    }
    Object.defineProperty(DiffuseCelMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return DiffuseCelMethod.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DiffuseCelMethod.prototype, "levels", {
        /**
         * The amount of shadow gradations.
         */
        get: function () {
            return this._levels;
        },
        set: function (value) {
            this._levels = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(DiffuseCelMethod.prototype, "smoothness", {
        /**
         * The smoothness of the edge between 2 shading levels.
         */
        get: function () {
            return this._smoothness;
        },
        set: function (value) {
            this._smoothness = value;
        },
        enumerable: false,
        configurable: true
    });
    DiffuseCelMethod.assetType = '[asset DiffuseCelMethod]';
    return DiffuseCelMethod;
}(DiffuseCompositeMethod));
export { DiffuseCelMethod };
import { ShaderBase } from '@awayjs/renderer';
import { _Shader_LightingCompositeMethod } from './CompositeMethodBase';
/**
 * _Shader_DiffuseCelMethod provides a shading method to add diffuse cel (cartoon) shading.
 */
var _Shader_DiffuseCelMethod = /** @class */ (function (_super) {
    __extends(_Shader_DiffuseCelMethod, _super);
    /**
     * Creates a new _Shader_DiffuseCelMethod object.
     * @param levels The amount of shadow gradations.
     * @param baseMethod An optional diffuse method on which the cartoon shading is based. If omitted, DiffuseBasicMethod is used.
     */
    function _Shader_DiffuseCelMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        _this._baseChunk._modulateFunction = function (targetReg, registerCache, sharedRegisters) { return _this.clampDiffuse(targetReg, registerCache, sharedRegisters); };
        return _this;
    }
    /**
     * @inheritDoc
     */
    _Shader_DiffuseCelMethod.prototype._initConstants = function () {
        _super.prototype._initConstants.call(this);
        var data = this._shader.fragmentConstantData;
        var index = this._clampDataIndex;
        data[index + 1] = 1;
        data[index + 2] = 0;
    };
    /**
     * @inheritDoc
     */
    _Shader_DiffuseCelMethod.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._clampDataRegister = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_DiffuseCelMethod.prototype._getFragmentPreLightingCode = function (registerCache, sharedRegisters) {
        this._clampDataRegister = registerCache.getFreeFragmentConstant();
        this._clampDataIndex = this._clampDataRegister.index * 4;
        return _super.prototype._getFragmentPreLightingCode.call(this, registerCache, sharedRegisters);
    };
    /**
     * @inheritDoc
     */
    _Shader_DiffuseCelMethod.prototype._activate = function () {
        _super.prototype._activate.call(this);
        if (this._invalid) {
            var data = this._shader.fragmentConstantData;
            var index = this._clampDataIndex;
            data[index] = this._method.levels;
            data[index + 3] = this._method.smoothness;
        }
    };
    /**
     * Snaps the diffuse shading of the wrapped method to one of the levels.
     * @param vo The MethodVO used to compile the current shader.
     * @param t The register containing the diffuse strength in the "w" component.
     * @param regCache The register cache used for the shader compilation.
     * @param sharedRegisters The shared register data for this shader.
     * @return The AGAL fragment code for the method.
     */
    _Shader_DiffuseCelMethod.prototype.clampDiffuse = function (targetReg, registerCache, sharedRegisters) {
        return 'mul ' + targetReg + '.w, ' + targetReg + '.w, ' + this._clampDataRegister + '.x\n' +
            'frc ' + targetReg + '.z, ' + targetReg + '.w\n' +
            'sub ' + targetReg + '.y, ' + targetReg + '.w, ' + targetReg + '.z\n' +
            'mov ' + targetReg + '.x, ' + this._clampDataRegister + '.x\n' +
            'sub ' + targetReg + '.x, ' + targetReg + '.x, ' + this._clampDataRegister + '.y\n' +
            'rcp ' + targetReg + '.x,' + targetReg + '.x\n' +
            'mul ' + targetReg + '.w, ' + targetReg + '.y, ' + targetReg + '.x\n' +
            // previous clamped strength
            'sub ' + targetReg + '.y, ' + targetReg + '.w, ' + targetReg + '.x\n' +
            // fract/epsilon (so 0 - epsilon will become 0 - 1)
            'div ' + targetReg + '.z, ' + targetReg + '.z, ' + this._clampDataRegister + '.w\n' +
            'sat ' + targetReg + '.z, ' + targetReg + '.z\n' +
            'mul ' + targetReg + '.w, ' + targetReg + '.w, ' + targetReg + '.z\n' +
            // 1-z
            'sub ' + targetReg + '.z, ' + this._clampDataRegister + '.y, ' + targetReg + '.z\n' +
            'mul ' + targetReg + '.y, ' + targetReg + '.y, ' + targetReg + '.z\n' +
            'add ' + targetReg + '.w, ' + targetReg + '.w, ' + targetReg + '.y\n' +
            'sat ' + targetReg + '.w, ' + targetReg + '.w\n';
    };
    return _Shader_DiffuseCelMethod;
}(_Shader_LightingCompositeMethod));
export { _Shader_DiffuseCelMethod };
ShaderBase.registerAbstraction(_Shader_DiffuseCelMethod, DiffuseCelMethod);
